import * as React from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MultiComboboxOption {
  id: number;
  label: string;
  disabled?: boolean;
}

export interface MultiComboboxProps {
  options: MultiComboboxOption[];
  selectedIds: readonly number[];
  onSelectedIdsChange: (ids: readonly number[]) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  selectAllLabel: string;
  clearAllLabel: string;
  selectedLabel: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  "data-testid"?: string;
}

function CheckboxCommandItem({
  checked,
  ...props
}: React.ComponentProps<typeof CommandItem> & { checked: boolean }): React.ReactElement {
  const itemRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    // cmdk hardcodes role="option" after consumer props; restore the set-valued checkbox contract.
    itemRef.current?.setAttribute("role", "checkbox");
  });

  return <CommandItem ref={itemRef} aria-checked={checked} {...props} />;
}

export function MultiCombobox({
  options,
  selectedIds,
  onSelectedIdsChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  selectAllLabel,
  clearAllLabel,
  selectedLabel,
  disabled,
  className,
  contentClassName,
  "data-testid": testId,
}: MultiComboboxProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const selectedIdSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleOptions = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(normalizedQuery),
  );
  const selectedCount = selectedIdSet.size;
  const triggerLabel =
    selectedCount === 0
      ? placeholder
      : selectedLabel
          .replace("{{selected}}", String(selectedCount))
          .replace("{{total}}", String(options.length));

  function update(mutator: (next: Set<number>) => void): void {
    const next = new Set(selectedIdSet);
    mutator(next);
    onSelectedIdsChange([...next].sort((a, b) => a - b));
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label={placeholder}
          disabled={disabled}
          data-testid={testId}
          className={cn("w-full justify-between", className)}
        >
          <span className={cn("min-w-0 truncate", selectedCount === 0 && "text-muted-foreground")}>
            {triggerLabel}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("w-(--radix-popover-trigger-width) p-0", contentClassName)}
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
            disabled={disabled}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <div className="flex items-center justify-between gap-2 border-b border-[var(--rule-line)] px-2 py-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-1.5 text-xs"
              disabled={disabled}
              onClick={() =>
                update((next) => visibleOptions.forEach((option) => next.add(option.id)))
              }
            >
              {selectAllLabel}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-1.5 text-xs"
              disabled={disabled}
              onClick={() =>
                update((next) => visibleOptions.forEach((option) => next.delete(option.id)))
              }
            >
              {clearAllLabel}
            </Button>
          </div>
          <CommandList
            className="overscroll-contain"
            data-testid={testId ? `${testId}-list` : undefined}
          >
            {visibleOptions.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
            {visibleOptions.map((option) => {
              const checked = selectedIdSet.has(option.id);
              return (
                <CheckboxCommandItem
                  key={option.id}
                  value={option.label}
                  checked={checked}
                  aria-label={option.label}
                  disabled={disabled || option.disabled}
                  data-testid={testId ? `${testId}-option-${option.id}` : undefined}
                  onSelect={() =>
                    update((next) => (checked ? next.delete(option.id) : next.add(option.id)))
                  }
                  className="flex items-center gap-1.5"
                >
                  <span
                    className="flex h-4 w-4 shrink-0 items-center justify-center border border-[color:var(--rule-strong)]"
                    aria-hidden
                  >
                    {checked && <CheckIcon className="size-3" />}
                  </span>
                  <span className="min-w-0 truncate">{option.label}</span>
                </CheckboxCommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
